import { Injectable, NgZone, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, tap, catchError, map, throwError, of, finalize, shareReplay, switchMap, timer } from 'rxjs';
import { LoginRequest, LoginResponse, Business } from '../models/sala.model';
import { environment } from '../../environments/environment';
import { currentFrontendBaseUrl } from '../utils/frontend-base-url';
import { EMBEDDED_BUSINESS_CONTEXT_ID, isBusinessContextMessage, postToBusinessContextHost } from '../utils/business-context';

/** Sesión extendida de respaldo (sin cookie), solo en el almacenamiento de ESTA pestaña. */
const FALLBACK_KEY = 'tusala_admin_fallback';
interface FallbackSession { token: string; expiresAt: number; business: Business; }

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private accessToken: string | null = null;
  /**
   * Modo de respaldo: el navegador no guarda la cookie de renovación (Safari/
   * iPhone y navegadores que bloquean cookies de terceros; la API vive en otro
   * dominio). Se pide UNA sola extensión justo después de iniciar sesión (8 h
   * desde el login) y se guarda solo en esta pestaña para sobrevivir a una
   * recarga; al cerrar la pestaña o al vencer, se vuelve a iniciar sesión.
   */
  private fallbackExpiresAt: number | null = null;
  private sessionValidated = false;
  private sessionValidationRequest?: Observable<boolean>;
  private refreshRequest?: Observable<boolean>;
  private refreshFailureTerminal = false;
  private sessionGeneration = 0;
  private logoutRequest?: Observable<unknown>;

  /**
   * "Gestionar negocio": negocio que un super admin opera desde el modal de
   * Super Admin (solo dentro de ese iframe; null en el panel normal). En este
   * modo la sesión es la del super admin, prestada por la ventana principal:
   * aquí nunca se usa la cookie de renovación ni el respaldo en sessionStorage.
   */
  readonly businessContextId: number | null = EMBEDDED_BUSINESS_CONTEXT_ID;
  private contextRequest?: Observable<boolean>;
  private hostTokenRequestSeq = 0;
  private readonly pendingHostTokenRequests = new Map<number, (token: string | null) => void>();

  // Señales reactivas
  public readonly isAuthenticated = signal<boolean>(false);
  public readonly currentUser = signal<Business | null>(null);
  public readonly trialExpired = signal<boolean>(false);
  // Igual que trialExpired, pero para un plan PAGADO cancelado cuyo período
  // de gracia ya terminó (no una prueba gratis) -- código de backend
  // SUBSCRIPTION_RESTRICTED, ver auth.interceptor.ts. Señal separada a
  // propósito: no reutiliza trialExpired para no arriesgar su lógica ya
  // probada, aunque ambas se muestran con el mismo tipo de superposición
  // (ver app.html).
  public readonly subscriptionRestricted = signal<boolean>(false);

  /** Cerrar sesión en una pestaña la cierra en todas las del mismo navegador
   *  (el access token vive en memoria de cada pestaña hasta 30 min). */
  private readonly sessionChannel = typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel('tusala-admin-session') : null;
  private readonly router = inject(Router);
  private readonly zone = inject(NgZone);

  constructor(private http: HttpClient) {
    this.clearLegacyStorage();
    if (this.businessContextId) {
      // La ventana principal decide sobre la sesión (cierre, renovación).
      window.addEventListener('message', event => this.onBusinessContextHostMessage(event));
      return;
    }
    this.sessionChannel?.addEventListener('message', event => {
      if (event.data !== 'logout' || !this.isAuthenticated()) return;
      this.zone.run(() => {
        this.sessionGeneration++;
        this.clearLocalSession();
        void this.router.navigate(['/login']);
      });
    });
  }

  login(email: string, password: string, code?: string, rememberMe = false): Observable<boolean> {
    const loginRequest: LoginRequest & { rememberMe: boolean } = { email, password, rememberMe, ...(code ? { code } : {}) };
    
    // Utiliza el endpoint SaaS owner-login que soporta tanto negocios nuevos
    // (User en tabla Users con role='owner') como el negocio existente.
    const ready = this.logoutRequest || of(null);
    return ready.pipe(switchMap(() => this.http.post<LoginResponse>(`${environment.apiUrl}/saas/owner-login`, loginRequest, { withCredentials: true }))).pipe(
      tap(response => {
        this.sessionGeneration++;
        this.refreshFailureTerminal = false;
        // Guardar el token (limpiar "Bearer" si viene incluido)
        const cleanToken = response.token.replace(/^Bearer\s+/i, '');
        this.accessToken = cleanToken;
        // El bloqueo de prueba vencida se recalcula siempre desde cero en cada
        // login (nunca se hereda de una sesión anterior en la misma pestaña,
        // p. ej. si el usuario navegó a /login sin pasar por logout()). Si el
        // login mismo ya indica una prueba vencida, se activa de inmediato en
        // vez de esperar a que la primera llamada bloqueada dispare el 403.
        this.applySubscriptionFlags(response.business);
        this.clearFallback();
        this.setSession(response.business);
        this.sessionValidated = true;
        this.ensureRenewableSession(this.sessionGeneration);
      }),
      map(() => true),
      catchError(error => {
        console.error('Error al iniciar sesión:', error);
        return throwError(() => error);
      })
    );
  }

  logout(): void {
    // Dentro de "Gestionar negocio" salir solo cierra el modal: la sesión es
    // la del super admin y sigue abierta en su ventana.
    if (this.businessContextId) {
      postToBusinessContextHost({ type: 'close' });
      return;
    }
    this.sessionGeneration++;
    // Con el token en la cabecera, el servidor también revoca la sesión
    // extendida de respaldo (si la hay); la cookie (si la hay) va aparte.
    const token = this.accessToken;
    const headers = token ? new HttpHeaders({ Authorization: `Bearer ${token}` }) : undefined;
    const request = this.http.post(`${environment.apiUrl}/saas/session/logout`, {}, { withCredentials: true, headers }).pipe(
      catchError(() => of(null)),
      finalize(() => { if (this.logoutRequest === request) this.logoutRequest = undefined; }),
      shareReplay({ bufferSize: 1, refCount: false }),
    );
    this.logoutRequest = request;
    // Las demás pestañas se enteran cuando el servidor YA revocó la cookie: si
    // se avisara antes, otra pestaña podría restaurarse con la cookie todavía
    // válida (y rotarla) justo antes de la revocación, y quedar con sesión.
    request.subscribe({ complete: () => this.sessionChannel?.postMessage('logout') });
    this.clearLocalSession();
    this.clearLegacyStorage();
  }

  private clearLocalSession(): void {
    this.clearFallback();
    this.accessToken = null;
    this.isAuthenticated.set(false);
    this.currentUser.set(null);
    this.trialExpired.set(false);
    this.subscriptionRestricted.set(false);
    this.sessionValidated = false;
    this.sessionValidationRequest = undefined;
    this.refreshRequest = undefined;
  }

  /**
   * Comprueba la sesión en memoria y, después de una recarga, la restaura
   * mediante la cookie HttpOnly antes de montar una pantalla interna.
   */
  validateStoredSession(): Observable<boolean> {
    if (this.businessContextId) {
      return this.getToken() && this.currentUser() ? of(true) : this.loadBusinessContext();
    }
    if (!this.getToken() || !this.currentUser()) return this.refreshSession();
    if (this.sessionValidated) return of(true);
    if (this.sessionValidationRequest) return this.sessionValidationRequest;

    this.sessionValidationRequest = this.http
      .get<{ valid: boolean }>(`${environment.apiUrl}/saas/session/validate`)
      .pipe(
        map(response => response.valid === true),
        tap(valid => {
          this.sessionValidated = valid;
          if (!valid) this.clearLocalSession();
        }),
        catchError(error => {
          this.refreshFailureTerminal = Number(error?.status) === 401;
          if (this.refreshFailureTerminal) this.clearLocalSession();
          return of(false);
        }),
        finalize(() => { this.sessionValidationRequest = undefined; }),
        shareReplay({ bufferSize: 1, refCount: false }),
      );
    return this.sessionValidationRequest;
  }

  confirmPasswordRecovery(token: string, password: string): Observable<{ msg: string }> {
    return this.http.post<{ msg: string }>(`${environment.apiUrl}/auth/password-recovery/confirm`, { token, password });
  }

  // Corrección pedida: antes, reabrir un enlace YA USADO seguía mostrando el
  // formulario de nueva contraseña -- el error solo se descubría al enviarlo.
  // Este endpoint (público, ya existente en el backend) deja validar el
  // token ANTES de mostrar nada. Responde 200 si es válido; 410 con
  // { status: 'used'|'expired'|'invalid', platformSupport } si no.
  validatePasswordRecoveryToken(token: string): Observable<{ status: string }> {
    return this.http.get<{ status: string }>(`${environment.apiUrl}/auth/password-recovery/validate`, { params: { token } });
  }

  requestPasswordRecovery(email: string): Observable<{ msg: string }> {
    return this.http.post<{ msg: string }>(`${environment.apiUrl}/saas/admin/password-recovery/request`, {
      email,
      frontend_base_url: currentFrontendBaseUrl(),
    });
  }

  completeOnboarding(): Observable<{ msg: string }> {
    return this.http.patch<{ msg: string }>(`${environment.apiUrl}/saas/onboarding/complete`, {}).pipe(
      tap(() => {
        const current = this.currentUser();
        if (current) this.updateCurrentUser({ ...current, onboarding_required: false });
      }),
    );
  }

  getToken(): string | null {
    return this.accessToken;
  }

  // ---------------------------------------------------------------------
  // 2FA (solo super_admin) -- antes setup-2fa/verify-2fa existían en el
  // backend pero eran inalcanzables desde la sesión real del panel (ver
  // saas.routes.ts) y no había ninguna pantalla para usarlos ni para
  // desactivar 2FA una vez activado. Estos métodos son los únicos puntos
  // de entrada del front a esos endpoints, ahora corregidos.
  // ---------------------------------------------------------------------
  getTwoFactorStatus(): Observable<{ enabled: boolean }> {
    return this.http.get<{ enabled: boolean }>(`${environment.apiUrl}/saas/admin/2fa/status`);
  }

  setupTwoFactor(): Observable<{ secret: string; qrCode: string; msg: string }> {
    return this.http.post<{ secret: string; qrCode: string; msg: string }>(`${environment.apiUrl}/saas/admin/2fa/setup`, {});
  }

  verifyTwoFactor(code: string): Observable<{ msg: string }> {
    return this.http.post<{ msg: string }>(`${environment.apiUrl}/saas/admin/2fa/verify`, { code }).pipe(
      tap(() => {
        const current = this.currentUser();
        if (current) this.updateCurrentUser({ ...current, totp_enabled: true });
      }),
    );
  }

  disableTwoFactor(currentPassword: string, code?: string): Observable<{ msg: string }> {
    return this.http.post<{ msg: string }>(`${environment.apiUrl}/saas/admin/2fa/disable`, { current_password: currentPassword, code }).pipe(
      tap(() => {
        const current = this.currentUser();
        if (current) this.updateCurrentUser({ ...current, totp_enabled: false });
      }),
    );
  }

  hasPermission(module: string, action = 'view'): boolean {
    const user = this.currentUser();
    if (!user) return false;
    if (user.role === 'super_admin') return true;
    if (user.role === 'owner') {
      // 'employees' aquí cubre TODO el módulo de sub-administradores
      // (empleados, propietarios delegados y superadmins) -- un propietario
      // delegado (no registrado) comparte el resto del acceso operativo,
      // pero jamás puede administrar accesos administrativos. Ver
      // requireCanonicalOwnerOrSuperAdmin en el backend (fuente real de verdad).
      if (module === 'employees') return !!user.can_manage_admins;
      return true;
    }
    return !!user.permissions?.[module]?.includes(action);
  }

  /** true solo para el propietario registrado del negocio o super_admin. */
  canManageAdmins(): boolean {
    return !!this.currentUser()?.can_manage_admins;
  }

  updateCurrentUser(business: Business): void {
    const current = this.currentUser();
    const merged: Business = {
      ...(current || {} as Business),
      ...business,
      role: business.role ?? current?.role,
      subscription: business.subscription ?? current?.subscription ?? null,
      can_manage_admins: business.can_manage_admins ?? current?.can_manage_admins,
    };
    // "Gestionar negocio": las respuestas del backend (perfil, sitio) traen el
    // rol real de quien pide (super_admin); la vista debe seguir siendo la del
    // propietario, así que la identidad de la sesión se conserva.
    if (this.businessContextId && current) {
      merged.role = current.role;
      merged.permissions = current.permissions;
      merged.can_manage_admins = current.can_manage_admins;
      merged.subscription_exempt = current.subscription_exempt;
      (merged as any).business_context = (current as any).business_context;
    }
    this.setSession(merged);
  }

  private setSession(business: Business): void {
    this.isAuthenticated.set(true);
    this.currentUser.set(business);
    if (this.fallbackExpiresAt) this.persistFallback();
  }

  restoreSession(): Observable<boolean> {
    if (this.businessContextId) return this.loadBusinessContext();
    // Modo de respaldo (sin cookie): la sesión extendida de esta pestaña. El
    // guard la valida contra el servidor antes de mostrar el panel.
    const fallback = this.readFallback();
    if (fallback) {
      this.accessToken = fallback.token;
      this.fallbackExpiresAt = fallback.expiresAt;
      this.applySubscriptionFlags(fallback.business);
      this.setSession(fallback.business);
      this.sessionValidated = false;
      return of(true);
    }
    return this.refreshSession();
  }

  /**
   * Justo después de iniciar sesión: ¿el navegador guardó la cookie? Si la
   * renovación responde NO_REFRESH_COOKIE, se pide la sesión extendida de
   * respaldo (una sola vez; el servidor solo la concede en los primeros
   * minutos tras el login y nunca más allá de 8 h desde ese momento).
   */
  private ensureRenewableSession(generation: number): void {
    const refreshUrl = `${environment.apiUrl}/saas/session/refresh`;
    this.http.post<LoginResponse>(refreshUrl, {}, { withCredentials: true }).pipe(
      tap(response => {
        if (generation !== this.sessionGeneration) return;
        this.accessToken = response.token.replace(/^Bearer\s+/i, '');
        this.setSession(response.business);
      }),
      map(() => null),
      catchError(error => {
        if (generation !== this.sessionGeneration || Number(error?.status) !== 401 || error?.error?.code !== 'NO_REFRESH_COOKIE' || !this.accessToken) return of(null);
        const headers = new HttpHeaders({ Authorization: `Bearer ${this.accessToken}` });
        return this.http.post<{ token: string; expires_at: string }>(`${environment.apiUrl}/saas/session/extend`, {}, { headers }).pipe(
          tap(response => {
            if (generation !== this.sessionGeneration) return;
            this.accessToken = response.token.replace(/^Bearer\s+/i, '');
            this.fallbackExpiresAt = Date.parse(response.expires_at);
            this.persistFallback();
          }),
          map(() => null),
          catchError(() => of(null)),
        );
      }),
    ).subscribe();
  }

  /**
   * Única regla para las superposiciones de acceso (login, renovación y
   * restauración usan esta misma función). El plan debe ser 'gratis' además
   * del status/trial_ends_at -- una suscripción con un plan pagado real puede
   * conservar un trial_ends_at viejo de cuando empezó como prueba (p. ej. si se
   * le cambió el plan manualmente desde Super Admin); sin este chequeo un
   * cliente que paga ve la superposición de "tu prueba terminó" en vez de la
   * de "tu plan pagado está restringido".
   */
  private applySubscriptionFlags(business: Business | null | undefined): void {
    // El super admin opera el negocio aunque su plan esté vencido o
    // restringido: el estado se informa en el modal, sin bloquear la vista.
    if (this.businessContextId) {
      this.trialExpired.set(false);
      this.subscriptionRestricted.set(false);
      return;
    }
    const sub = business?.subscription;
    this.trialExpired.set(sub?.plan === 'gratis' && sub?.status === 'expired' && !!sub?.trial_ends_at);
    this.subscriptionRestricted.set(sub?.status === 'expired' && !(sub?.plan === 'gratis' && !!sub?.trial_ends_at));
  }

  /** true mientras la sesión depende de la extensión de respaldo (sin cookie). */
  isFallbackSession(): boolean {
    return !!this.fallbackExpiresAt;
  }

  private persistFallback(): void {
    const business = this.currentUser();
    if (!this.accessToken || !this.fallbackExpiresAt || !business) return;
    const data: FallbackSession = { token: this.accessToken, expiresAt: this.fallbackExpiresAt, business };
    try { sessionStorage.setItem(FALLBACK_KEY, JSON.stringify(data)); } catch { /* sin almacenamiento: queda solo en memoria */ }
  }

  private readFallback(): FallbackSession | null {
    try {
      const raw = sessionStorage.getItem(FALLBACK_KEY);
      if (!raw) return null;
      const data = JSON.parse(raw) as FallbackSession;
      if (!data?.token || !data.business || !Number.isFinite(data.expiresAt) || data.expiresAt <= Date.now() + 5000) {
        sessionStorage.removeItem(FALLBACK_KEY);
        return null;
      }
      return data;
    } catch {
      return null;
    }
  }

  private clearFallback(): void {
    this.fallbackExpiresAt = null;
    try { sessionStorage.removeItem(FALLBACK_KEY); } catch { /* sin almacenamiento */ }
  }

  refreshSession(): Observable<boolean> {
    if (this.refreshRequest) return this.refreshRequest;
    if (this.businessContextId) return this.refreshBusinessContextToken();
    const generation = this.sessionGeneration;
    const request = () => this.http.post<LoginResponse>(`${environment.apiUrl}/saas/session/refresh`, {}, { withCredentials: true });
    this.refreshRequest = request().pipe(
      // 409 (y solo 409): otra pestaña renovó la cookie en el mismo instante.
      // La cookie nueva ya quedó en el navegador, así que UNA segunda petición
      // la usa. Cualquier otro error no se repite (ver wasLastRefreshTerminal).
      catchError(error => Number(error?.status) === 409 ? timer(400).pipe(switchMap(request)) : throwError(() => error)),
      tap(response => {
        if (generation !== this.sessionGeneration) return;
        this.refreshFailureTerminal = false;
        this.accessToken = response.token.replace(/^Bearer\s+/i, '');
        this.applySubscriptionFlags(response.business);
        this.setSession(response.business);
        this.sessionValidated = true;
      }),
      map(() => generation === this.sessionGeneration),
      catchError(error => {
        this.refreshFailureTerminal = Number(error?.status) === 401;
        if (this.refreshFailureTerminal) this.clearLocalSession();
        return of(false);
      }),
      finalize(() => { this.refreshRequest = undefined; }),
      shareReplay({ bufferSize: 1, refCount: false }),
    );
    return this.refreshRequest;
  }

  wasLastRefreshTerminal(): boolean {
    return this.refreshFailureTerminal;
  }

  /** true dentro del modal "Gestionar negocio" de Super Admin. */
  isBusinessContextMode(): boolean {
    return !!this.businessContextId;
  }

  /** Pide abrir, en la ventana del super admin, la gestión de la cuenta del propietario. */
  requestOwnerAccountManagement(): void {
    postToBusinessContextHost({ type: 'manage-owner-account' });
  }

  // ---------------------------------------------------------------------
  // "Gestionar negocio" (solo dentro del iframe)
  // ---------------------------------------------------------------------

  private onBusinessContextHostMessage(event: MessageEvent): void {
    if (event.source !== window.parent || event.origin !== window.location.origin) return;
    const data = event.data;
    if (!isBusinessContextMessage(data) || data.type !== 'token') return;
    const { requestId, token } = data as unknown as { requestId: number; token: string | null };
    this.pendingHostTokenRequests.get(Number(requestId))?.(typeof token === 'string' && token ? token : null);
  }

  /** Token vigente del super admin, prestado por la ventana principal. */
  private requestHostToken(refresh: boolean): Observable<string | null> {
    return new Observable<string | null>(subscriber => {
      const requestId = ++this.hostTokenRequestSeq;
      const finish = (token: string | null) => {
        clearTimeout(timeout);
        this.pendingHostTokenRequests.delete(requestId);
        this.zone.run(() => { subscriber.next(token); subscriber.complete(); });
      };
      const timeout = setTimeout(() => finish(null), 15000);
      this.pendingHostTokenRequests.set(requestId, finish);
      postToBusinessContextHost({ type: 'token-request', requestId, refresh });
      return () => {
        clearTimeout(timeout);
        this.pendingHostTokenRequests.delete(requestId);
      };
    });
  }

  private loadBusinessContext(): Observable<boolean> {
    if (this.contextRequest) return this.contextRequest;
    const businessId = this.businessContextId;
    this.contextRequest = this.requestHostToken(false).pipe(
      switchMap(token => {
        if (!token) return throwError(() => ({ status: 401, error: { msg: 'Tu sesión de Super Admin no está disponible. Vuelve a abrir el negocio.' } }));
        this.accessToken = token.replace(/^Bearer\s+/i, '');
        return this.http.get<{ business: Business }>(`${environment.apiUrl}/saas/admin/businesses/${businessId}/context`);
      }),
      tap(response => {
        this.refreshFailureTerminal = false;
        this.applySubscriptionFlags(response.business);
        this.setSession(response.business);
        this.sessionValidated = true;
        postToBusinessContextHost({ type: 'context-loaded', business: response.business });
      }),
      map(() => true),
      catchError(error => {
        this.refreshFailureTerminal = true;
        this.clearLocalSession();
        const message = String(error?.error?.msg || 'No fue posible abrir el negocio.');
        postToBusinessContextHost({ type: 'context-failed', message });
        return of(false);
      }),
      finalize(() => { this.contextRequest = undefined; }),
      shareReplay({ bufferSize: 1, refCount: false }),
    );
    return this.contextRequest;
  }

  private refreshBusinessContextToken(): Observable<boolean> {
    this.refreshRequest = this.requestHostToken(true).pipe(
      map(token => {
        if (!token) {
          this.refreshFailureTerminal = true;
          postToBusinessContextHost({ type: 'session-ended' });
          return false;
        }
        this.refreshFailureTerminal = false;
        this.accessToken = token.replace(/^Bearer\s+/i, '');
        return true;
      }),
      finalize(() => { this.refreshRequest = undefined; }),
      shareReplay({ bufferSize: 1, refCount: false }),
    );
    return this.refreshRequest;
  }

  private clearLegacyStorage(): void {
    for (const storage of [localStorage, sessionStorage]) {
      storage.removeItem('admin_session');
      storage.removeItem('admin_token');
    }
  }
}
